/** @jsxRuntime classic */
/** @jsx jsx */
import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import SmartLinkIcon from '@atlaskit/icon/core/smart-link';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { TimeAgo } from '@confluence/time-ago';

import { RecentWorkListItemLayout } from '../layouts/RecentWorkListItemLayout.tsx';
import { recentWorkMessages } from '../recentWorkMessages.ts';

import { getTitle } from './EmojiContentIcon.tsx';
import { StatusLozenge } from './StatusLozenge.tsx';

const styles = cssMap({
	titleAndStatus: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	title: {
		flexGrow: 1,
		maxWidth: '100%',
		minWidth: 0,
	},
	titleText: {
		display: 'block',
		overflow: 'hidden',
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.semibold'),
		width: '100%',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	metadata: {
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		whiteSpace: 'nowrap',
	},
	separator: {
		flexShrink: 0,
		marginInlineStart: token('space.050'),
		marginInlineEnd: token('space.050'),
	},
	spaceLinkWrapper: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	agentMetadata: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		columnGap: token('space.050'),
	},
	agentPicture: {
		flexShrink: 0,
		objectFit: 'cover',
		borderRadius: token('radius.full'),
	},
	needsInputIcon: {
		marginInlineStart: token('space.025'),
	},
	sessionLocation: {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	needsInput: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		columnGap: token('space.100'),
		color: token('color.text.information'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	spaceLink: {
		overflow: 'hidden',
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		textOverflow: 'ellipsis',
		'&:visited': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

type RecentWorkContent = {
	id: string;
	type?: string;
	subType?: string | null;
	status?: string;
	title?: { text?: string; emoji?: string };
	links?: { webui?: string };
	space?: { name?: string; spaceKey?: string };
	lastAction?: { when?: string };
};

type RecentWorkListItemViewProps = {
	content: RecentWorkContent;
	onClick?: () => void;
	onSpaceClick?: () => void;
	action?: string;
	lastActionI18n?: MessageDescriptor;
};

const getContentIconColor = (type?: string, subType?: string | null) => {
	if ((type === 'page' && subType === 'live') || type === 'liveDoc') {
		return token('color.icon.accent.magenta');
	}
	if (type === 'page' || type === 'blogpost') {
		return token('color.icon.accent.blue');
	}
	if (type === 'whiteboard' || type === 'slide') {
		return token('color.icon.accent.teal');
	}
	if (type === 'database') {
		return token('color.icon.accent.purple');
	}
	if (type === 'embed') {
		return token('color.icon.accent.gray');
	}
	return undefined;
};

const getContentTypeMessage = (
	type: string | undefined,
	subType: string | null | undefined,
): MessageDescriptor | undefined => {
	if ((type === 'page' && subType === 'live') || type === 'liveDoc') {
		return recentWorkMessages.contentTypeLiveDoc;
	}
	const messages: Record<string, MessageDescriptor> = {
		page: recentWorkMessages.contentTypePage,
		blogpost: recentWorkMessages.contentTypeBlogPost,
		whiteboard: recentWorkMessages.contentTypeWhiteboard,
		database: recentWorkMessages.contentTypeDatabase,
		embed: recentWorkMessages.contentTypeSmartLink,
		slide: recentWorkMessages.contentTypeSlide,
	};
	return type ? messages[type] : undefined;
};

const getContentIcon = (type?: string, subType?: string | null, emoji?: string) =>
	emoji ? (
		<ContentIcon type={type || ''} subType={subType || ''} emoji={emoji} />
	) : type === 'embed' ? (
		<SmartLinkIcon
			label=""
			color={getContentIconColor(type, subType)}
			testId="recent-work-smart-link-icon"
		/>
	) : (
		<ContentIcon
			type={type || ''}
			subType={subType || ''}
			color={getContentIconColor(type, subType)}
		/>
	);

export const RecentWorkListItemView: FC<RecentWorkListItemViewProps> = ({
	content,
	onClick,
	onSpaceClick,
	action,
	lastActionI18n,
}) => {
	const { formatMessage } = useIntl();
	const isLivePage =
		content.type === 'page' && content.subType === 'live' && content.status !== 'archived';
	const url = `${CONTEXT_PATH}${content.links?.webui}`;
	const title = getTitle(content.title?.text, Boolean(content.title?.emoji));
	const titleContent = title || formatMessage(recentWorkMessages.contentUntitled);
	const contentTypeMessage = getContentTypeMessage(content.type, content.subType);
	const space = content.space;

	const primary = (
		<div css={styles.titleAndStatus}>
			<Tooltip content={titleContent} position="bottom">
				{(tooltipProps) => (
					<Box xcss={styles.title} testId="recent-card-title" {...tooltipProps}>
						<span css={styles.titleText}>{titleContent}</span>
					</Box>
				)}
			</Tooltip>
			<StatusLozenge status={content.status || ''} />
		</div>
	);

	const secondary = (
		<div css={styles.metadata}>
			{contentTypeMessage && <FormattedMessage {...contentTypeMessage} />}
			{contentTypeMessage && space?.name && (
				<Box as="span" xcss={styles.separator} aria-hidden="true">
					·
				</Box>
			)}
			{space?.name && (
				<Box xcss={styles.spaceLinkWrapper}>
					<SpacesIcon
						label=""
						size="small"
						color={token('color.icon.subtle')}
						testId="recent-work-space-icon"
					/>
					{space.spaceKey ? (
						<Anchor
							xcss={styles.spaceLink}
							href={SPACE_OVERVIEW.toUrl({ spaceKey: space.spaceKey })}
							onClick={onSpaceClick}
						>
							{space.name}
						</Anchor>
					) : (
						space.name
					)}
				</Box>
			)}
		</div>
	);

	const icon = getContentIcon(content.type, content.subType, content.title?.emoji);

	const card = (
		<RecentWorkListItemLayout
			url={url}
			ariaLabel={titleContent}
			onClick={onClick}
			testId="recent-work-list-item"
			icon={icon}
			secondary={secondary}
			lastModified={
				content.lastAction?.when ? (
					<Fragment>
						{lastActionI18n && <FormattedMessage {...lastActionI18n} />}{' '}
						<TimeAgo date={content.lastAction.when} />
					</Fragment>
				) : undefined
			}
			actions={action}
		>
			{primary}
		</RecentWorkListItemLayout>
	);

	return (
		<LivePagePreloader isLivePage={isLivePage}>
			<HoverPreloader
				name="recent-work-list-item"
				preloadUrl={url}
				preloadOptions={PreloadingPresets.onlyJS}
			>
				{card}
			</HoverPreloader>
		</LivePagePreloader>
	);
};

type AgentSessionListItemViewProps = {
	title?: string | null;
	url?: string;
	agentName?: string | null;
	agentPicture?: string | null;
	contentType?: string;
	contentSubType?: string | null;
	space?: { name?: string | null; spaceKey?: string | null } | null;
};

export const AgentSessionListItemView: FC<AgentSessionListItemViewProps> = ({
	title,
	url,
	agentName,
	agentPicture,
	contentType,
	contentSubType,
	space,
}) => {
	const { formatMessage } = useIntl();
	const titleContent = title || formatMessage(recentWorkMessages.agentSession);
	const resolvedAgentName = agentName || formatMessage(recentWorkMessages.rovoAgent);
	const contentTypeMessage =
		getContentTypeMessage(contentType, contentSubType) || recentWorkMessages.chat;
	const primary = (
		<Tooltip content={titleContent} position="bottom">
			{(tooltipProps) => (
				<Box xcss={styles.title} testId="agent-session-card-title" {...tooltipProps}>
					<span css={styles.titleText}>{titleContent}</span>
				</Box>
			)}
		</Tooltip>
	);
	const secondary = (
		<div css={styles.metadata}>
			<span css={styles.agentMetadata}>
				{agentPicture && (
					<img
						css={styles.agentPicture}
						src={agentPicture}
						width={12}
						height={12}
						alt=""
						data-testid="agent-session-agent-picture"
					/>
				)}
				<span data-testid="agent-session-agent-name">{resolvedAgentName}</span>
			</span>
			<Box as="span" xcss={styles.separator} aria-hidden="true">
				·
			</Box>
			<FormattedMessage {...contentTypeMessage} />
			{space?.name && (
				<span css={styles.sessionLocation}>
					<Box as="span" xcss={styles.separator} aria-hidden="true">
						·
					</Box>
					<Box as="span" xcss={styles.spaceLinkWrapper}>
						<SpacesIcon
							label=""
							size="small"
							color={token('color.icon.subtle')}
							testId="agent-session-space-icon"
						/>
						{space.spaceKey ? (
							<Anchor
								xcss={styles.spaceLink}
								href={SPACE_OVERVIEW.toUrl({ spaceKey: space.spaceKey })}
							>
								{space.name}
							</Anchor>
						) : (
							space.name
						)}
					</Box>
				</span>
			)}
		</div>
	);
	const row = (
		<RecentWorkListItemLayout
			url={url}
			ariaLabel={titleContent}
			testId="agent-session-list-item"
			icon={
				contentType ? (
					getContentIcon(contentType, contentSubType)
				) : (
					<RovoChatIcon
						label=""
						color={token('color.icon.subtle')}
						testId="agent-session-chat-icon"
					/>
				)
			}
			secondary={secondary}
			actions={
				<div css={styles.needsInput}>
					<FormattedMessage {...recentWorkMessages.needsInput} />
					<Box xcss={styles.needsInputIcon}>
						<StatusInformationIcon
							label=""
							size="medium"
							color={token('color.icon.information')}
							testId="agent-session-needs-input-icon"
						/>
					</Box>
				</div>
			}
		>
			{primary}
		</RecentWorkListItemLayout>
	);

	return url ? (
		<HoverPreloader
			name="agent-session-list-item"
			preloadUrl={url}
			preloadOptions={PreloadingPresets.onlyJS}
		>
			{row}
		</HoverPreloader>
	) : (
		row
	);
};
